import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Icon, Loader } from "semantic-ui-react";
import { formatDate } from "../../constants";
import { selectCurrentToken } from "../auth/authSlice";
import { uploadFileToS3Presigned } from "../../utils/presignedUpload";
import {
  Badge,
  InfoNote,
  Pill,
  cardStyle,
  fieldStyle,
  labelStyle,
  ui,
} from "../../components/adminUi";
import {
  Attachment,
  CenteredLoader,
  ErrorNote,
  Page,
  PageBody,
  PageHeader,
} from "./supportTicketChrome";
import {
  useCloseSupportTicketMutation,
  useCreateSupportReplyMutation,
  useGetSupportTicketByIdQuery,
  useGetSupportTicketImageUrlQuery,
} from "./supportTicketApiSlice";
import { getSupportTicketStatusLabel, isReporterReply } from "./supportTicketUtils";

/**
 * 지원 요청 상세, in the same visual language as the list and 대화 내역.
 *
 * The conversation itself keeps `.message-row` / `.message-bubble` from index.css — the same
 * classes the roleplay transcript uses. Those were already the right language for a thread
 * of messages, so they are untouched; everything around them is what read as a different
 * product: `<Header as="h2">`, three stacked `<Segment>`s and Semantic form controls.
 *
 * `maxWidth` is deliberate and unlike the list pages. This is a reading surface — a thread
 * plus a compose box — and full-bleed 16px text across 1600px is not readable.
 */
const CONTENT_MAX = "900px";

const MetaRow = ({ label, children }) => (
  <div style={{ display: "flex", gap: "10px", fontSize: "13px", lineHeight: 1.6 }}>
    <span style={{ color: ui.muted, minWidth: "58px", flexShrink: 0 }}>{label}</span>
    <span style={{ color: ui.text, minWidth: 0, wordBreak: "break-word" }}>{children}</span>
  </div>
);

const AdminSupportTicketDetailPage = () => {
  const { id } = useParams();
  const token = useSelector(selectCurrentToken);
  const [replyBody, setReplyBody] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const { data: ticket, isLoading, isError } = useGetSupportTicketByIdQuery(id);

  const { data: imageData, isLoading: isImageLoading } = useGetSupportTicketImageUrlQuery(
    id,
    { skip: !ticket?.image_s3_key }
  );

  const [createSupportReply, { isLoading: isReplying }] = useCreateSupportReplyMutation();
  const [closeSupportTicket, { isLoading: isClosing }] = useCloseSupportTicketMutation();

  const isClosed = ticket?.status === "closed";
  const busy = isReplying || isClosing || isUploading;

  const handleReplySubmit = async () => {
    if (!replyBody.trim()) {
      setError("답변 내용을 입력해 주세요.");
      return;
    }

    setError(null);
    let imageS3Key = null;
    if (imageFile) {
      setIsUploading(true);
      const uploadResult = await uploadFileToS3Presigned(imageFile, token);
      setIsUploading(false);
      if (!uploadResult.success) {
        setError(uploadResult.error || "이미지 업로드에 실패했습니다.");
        return;
      }
      imageS3Key = uploadResult.key;
    }

    const result = await createSupportReply({
      ticketId: id,
      body: replyBody.trim(),
      ...(imageS3Key ? { image_s3_key: imageS3Key } : {}),
    });
    if (result.error) {
      setError("답변 등록에 실패했습니다.");
      return;
    }
    setReplyBody("");
    setImageFile(null);
  };

  const handleClose = async () => {
    setError(null);
    const result = await closeSupportTicket(id);
    if (result.error) {
      setError("요청 종료에 실패했습니다.");
    }
  };

  if (isLoading) {
    return (
      <Page>
        <CenteredLoader />
      </Page>
    );
  }

  if (isError || !ticket) {
    return (
      <Page>
        <PageBody maxWidth={CONTENT_MAX}>
          <div style={{ paddingTop: "40px" }}>
            <ErrorNote>요청을 불러오지 못했습니다.</ErrorNote>
        {/* Wrapped in a Link rather than `<Pill as={Link}>`: Pill renders a <button> and
            spreads the rest onto it, so `as`/`to` would land as invalid DOM attributes and
            the thing would look like a link and do nothing. */}
            <Link to="/admin/support-tickets">
              <Pill variant="neutral">
                <Icon name="arrow left" style={{ margin: 0 }} /> 목록으로
              </Pill>
            </Link>
          </div>
        </PageBody>
      </Page>
    );
  }

  const status = getSupportTicketStatusLabel(ticket.status);
  const replies = ticket.replies || [];
  const author = ticket.author?.full_name || ticket.author?.username;

  return (
    <Page>
      <PageHeader
        title="지원 요청 상세"
        subtitle={`${author} · ${formatDate(ticket.created_at)}`}
        backTo={<Link to="/admin/support-tickets" />}
        right={<Badge tone={status.tone}>{status.text}</Badge>}
      />

      <PageBody maxWidth={CONTENT_MAX}>
        {ticket.context && Object.keys(ticket.context).length > 0 && (
          <div style={{ ...cardStyle, padding: "14px 16px", marginBottom: "16px" }}>
            <MetaRow label="컨텍스트">
              {/* Kept as raw JSON: it is whatever the reporting screen attached, and an
                  admin debugging a report wants it verbatim, not summarised. */}
              <pre
                style={{
                  margin: 0,
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  color: ui.muted,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {JSON.stringify(ticket.context, null, 2)}
              </pre>
            </MetaRow>
          </div>
        )}

        <div style={{ ...cardStyle, padding: "16px", marginBottom: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div className="message-row received">
              <div className="message-bubble received">
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{author}</div>
                <p className="message-text">{ticket.body}</p>
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
                  {formatDate(ticket.created_at)}
                </div>
              </div>
            </div>

            {ticket.image_s3_key && (
              <div style={{ margin: "12px 0", maxWidth: "70%" }}>
                {isImageLoading && <Loader active inline size="small" />}
                {imageData?.url && <Attachment url={imageData.url} />}
              </div>
            )}

            {replies.map((reply) => {
              const reporterReply = isReporterReply(reply, ticket);
              return (
                <div
                  key={reply.id}
                  className={`message-row ${reporterReply ? "received" : "sent"}`}
                >
                  <div className={`message-bubble ${reporterReply ? "received" : "sent"}`}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                      {reply.author?.full_name || reply.author?.username}
                    </div>
                    <p className="message-text">{reply.body}</p>
                    {reply.image_url && (
                      <Attachment url={reply.image_url} style={{ marginTop: 8 }} />
                    )}
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
                      {formatDate(reply.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {isClosed ? (
          <InfoNote style={{ marginBottom: 0 }}>
            <Icon name="check circle outline" style={{ margin: 0, color: ui.accent }} />
            <span>종료된 요청입니다. 추가 답변 및 종료 작업을 할 수 없습니다.</span>
          </InfoNote>
        ) : (
          <div style={{ ...cardStyle, padding: "16px" }}>
            <label style={labelStyle}>답변 작성</label>
            <textarea
              style={{ ...fieldStyle, minHeight: "104px", resize: "vertical", lineHeight: 1.5 }}
              placeholder="사용자에게 보낼 답변을 입력해 주세요."
              value={replyBody}
              onChange={(event) => setReplyBody(event.target.value)}
            />

            <label style={{ ...labelStyle, marginTop: "14px" }}>이미지 첨부 (선택)</label>
            <input
              type="file"
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              onChange={(event) => {
                setImageFile(event.target.files?.[0] || null);
                setError(null);
              }}
              style={{ fontSize: "13px", color: ui.muted }}
            />
            {previewUrl && (
              <img
                src={previewUrl}
                alt="첨부 미리보기"
                style={{
                  maxWidth: "100%",
                  maxHeight: 200,
                  marginTop: 10,
                  borderRadius: 12,
                  display: "block",
                }}
              />
            )}

            {error && (
              <InfoNote
                style={{ background: ui.redBg, color: ui.redFg, margin: "14px 0 0" }}
              >
                <Icon name="exclamation triangle" style={{ margin: 0 }} />
                <span>{error}</span>
              </InfoNote>
            )}

            {/* Wraps, and 요청 종료 is pushed away from 답변 등록: they sit next to each
                other and one of them is irreversible. */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginTop: "16px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Pill
                variant="primary"
                disabled={busy}
                onClick={handleReplySubmit}
                style={{ height: "40px", padding: "0 20px" }}
              >
                {isUploading ? "이미지 업로드 중…" : isReplying ? "등록 중…" : "답변 등록"}
              </Pill>
              <Pill
                variant="danger"
                disabled={busy}
                onClick={handleClose}
                style={{ height: "40px", padding: "0 20px", marginLeft: "auto" }}
              >
                {isClosing ? "종료 중…" : "요청 종료"}
              </Pill>
            </div>
          </div>
        )}
      </PageBody>
    </Page>
  );
};

export default AdminSupportTicketDetailPage;
